/**
 * Hook for Web Push Notifications functionality
 * @module hooks/usePushNotifications
 * @version 4.10.5
 *
 * v4.10.5 - Re-validate push subscription on visibilitychange (foreground return)
 *           Ensures VAPID key migration happens without manual user action
 * v4.10.4 - Force resubscription when applicationServerKey is null (some browsers/PWAs)
 *           to prevent silently re-saving old subscriptions from a previous VAPID key
 * v4.10.3 - Detect VAPID key mismatch and auto-resubscribe
 * v4.10.2 - Revalidate subscription with server on every app open
 *           to fix stale endpoints after browser rotates push subscription
 * v4.10.1 - Push notifications only available in PWA context on mobile
 *           to prevent duplicate subscriptions from browser and PWA
 */

import { useState, useEffect, useCallback } from 'react';
import { getCurrentSession } from '../services/api/auth';
import { supabase, handleSupabaseError } from '../services/api/client';
import { isInstalledPWA, isMobileDevice } from '../utils/deviceUtils';

// VAPID public key from environment variable
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

/**
 * Helper to call RPC functions with error handling
 */
const callRpc = async (fnName: string, params: Record<string, any>): Promise<any> => {
  const { data, error } = await supabase.rpc(fnName, params);
  if (error) throw handleSupabaseError(error);
  return data;
};

export interface UsePushNotificationsReturn {
  /** Whether the browser supports push notifications */
  isSupported: boolean;
  /** Current notification permission status */
  permission: NotificationPermission;
  /** Whether user has an active push subscription */
  isSubscribed: boolean;
  /** Whether an operation is in progress */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Subscribe to push notifications */
  subscribe: () => Promise<boolean>;
  /** Unsubscribe from push notifications */
  unsubscribe: () => Promise<boolean>;
  /** Request notification permission only (without subscribing) */
  requestPermission: () => Promise<boolean>;
}

/**
 * Convert URL-safe base64 to Uint8Array for VAPID key
 */
const urlBase64ToUint8Array = (base64String: string): Uint8Array<ArrayBuffer> => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check browser support and configuration
    const checkSupport = async () => {
      // Browser must support notifications, service workers, and push
      const browserSupported =
        'Notification' in window &&
        'serviceWorker' in navigator &&
        'PushManager' in window;

      // Also need VAPID key to be configured
      const configured = !!VAPID_PUBLIC_KEY;

      if (!configured && browserSupported) {
        // Only log warning once if browser supports but not configured
        console.warn('[Push] VAPID_PUBLIC_KEY not configured - push notifications disabled');
      }

      // v4.10.1: Only support push in PWA context on mobile
      // This prevents duplicate subscriptions from browser and PWA
      // Desktop browsers are always allowed (for admin convenience)
      const isPWA = isInstalledPWA();
      const isMobile = isMobileDevice();
      const contextAllowed = !isMobile || isPWA; // Desktop always OK, mobile only PWA

      if (!contextAllowed && browserSupported && configured) {
        console.info('[Push] Push notifications only available in PWA mode on mobile devices');
      }

      const supported = browserSupported && configured && contextAllowed;
      setIsSupported(supported);

      if (supported) {
        setPermission(Notification.permission);
        await revalidateSubscription();
        // v4.10.2: Send push config to SW for pushsubscriptionchange handler
        sendPushConfigToSW();
      }
    };

    checkSupport();

    // v6.2.56: Re-validate subscription when app returns to foreground
    // Ensures users get migrated to new VAPID key without manual action
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        revalidateSubscription();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  /**
   * v4.10.3: Revalidate subscription with server on every app open.
   * Detects VAPID key rotation: if the existing subscription was created
   * with a different key than the current VAPID_PUBLIC_KEY, automatically
   * unsubscribes and creates a new subscription — transparent to the user.
   * Also handles the standard case (browser rotated the subscription).
   */
  const revalidateSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        setIsSubscribed(false);
        return;
      }

      // v4.10.4: Detect VAPID key mismatch via applicationServerKey
      // If applicationServerKey is null (some browsers/PWAs), force resubscription
      // to avoid silently re-saving an old subscription created with a different key
      const currentKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const storedKeyBuffer = subscription.options?.applicationServerKey as ArrayBuffer | null | undefined;
      const storedKey = storedKeyBuffer ? new Uint8Array(storedKeyBuffer) : null;

      const keyMismatch = storedKey === null || (
        storedKey.length !== currentKey.length ||
        !storedKey.every((b, i) => b === currentKey[i])
      );

      if (keyMismatch) {
        console.log(`[Push] VAPID key ${storedKey === null ? 'unknown (null)' : 'changed'} - auto-resubscribing...`);
        await subscription.unsubscribe();

        // Create a fresh subscription with the current key
        const newSub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: currentKey
        });

        const session = getCurrentSession();
        if (session?.token) {
          const subJson = newSub.toJSON();
          if (subJson.endpoint && subJson.keys?.p256dh && subJson.keys?.auth) {
            await callRpc('user_save_push_subscription', {
              p_session_token: session.token,
              p_endpoint: subJson.endpoint,
              p_p256dh_key: subJson.keys.p256dh,
              p_auth_key: subJson.keys.auth
            });
            setIsSubscribed(true);
            console.log('[Push] Auto-resubscribed with new VAPID key');
          }
        }
        return;
      }

      setIsSubscribed(true);

      // Re-save to server if user has an active session
      const session = getCurrentSession();
      if (!session?.token) return;

      const subJson = subscription.toJSON();
      if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) return;

      await callRpc('user_save_push_subscription', {
        p_session_token: session.token,
        p_endpoint: subJson.endpoint,
        p_p256dh_key: subJson.keys.p256dh,
        p_auth_key: subJson.keys.auth
      });

      console.log('[Push] Subscription revalidated with server');
    } catch (err) {
      console.error('[Push] Error revalidating subscription:', err);
    }
  };

  /**
   * v4.10.2: Send push config to SW so it can handle pushsubscriptionchange
   * even when the app is not open (config is stored in SW memory).
   */
  const sendPushConfigToSW = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.active) {
        registration.active.postMessage({
          type: 'PUSH_CONFIG',
          supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
          supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          vapidPublicKey: VAPID_PUBLIC_KEY
        });
      }
    } catch (err) {
      console.error('[Push] Error sending config to SW:', err);
    }
  };

  /**
   * Request notification permission from user
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Las notificaciones push no estan soportadas en este navegador');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (err) {
      console.error('[Push] Error requesting permission:', err);
      setError('Error al solicitar permiso de notificaciones');
      return false;
    }
  }, [isSupported]);

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Las notificaciones push no estan soportadas en este navegador');
      return false;
    }

    if (!VAPID_PUBLIC_KEY) {
      setError('Configuracion de notificaciones push incompleta');
      console.error('[Push] VAPID_PUBLIC_KEY not configured');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request permission if not granted
      if (Notification.permission !== 'granted') {
        const permissionGranted = await requestPermission();
        if (!permissionGranted) {
          setError('Necesitamos tu permiso para enviarte notificaciones');
          setIsLoading(false);
          return false;
        }
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Get session token
      const session = getCurrentSession();
      if (!session?.token) {
        setError('Debes iniciar sesion para activar notificaciones');
        setIsLoading(false);
        return false;
      }

      // Extract subscription keys
      const subscriptionJson = subscription.toJSON();

      if (!subscriptionJson.endpoint || !subscriptionJson.keys?.p256dh || !subscriptionJson.keys?.auth) {
        throw new Error('Subscription data incomplete');
      }

      // Save subscription to backend
      const result = await callRpc('user_save_push_subscription', {
        p_session_token: session.token,
        p_endpoint: subscriptionJson.endpoint,
        p_p256dh_key: subscriptionJson.keys.p256dh,
        p_auth_key: subscriptionJson.keys.auth
      });

      if (result?.success) {
        setIsSubscribed(true);
        console.log('[Push] Subscription saved successfully');
        return true;
      } else {
        throw new Error('Failed to save subscription');
      }

    } catch (err: any) {
      console.error('[Push] Error subscribing:', err);

      // Handle specific errors
      if (err.name === 'NotAllowedError') {
        setError('Permiso de notificaciones denegado');
      } else if (err.message?.includes('gcm_sender_id')) {
        setError('Error de configuracion del servidor');
      } else {
        setError('Error al activar notificaciones push');
      }

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, requestPermission]);

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        setIsSubscribed(false);
        return true;
      }

      // Unsubscribe from push manager
      const unsubscribed = await subscription.unsubscribe();

      if (!unsubscribed) {
        throw new Error('Failed to unsubscribe');
      }

      // Remove from backend
      const session = getCurrentSession();
      if (session?.token) {
        const subscriptionJson = subscription.toJSON();
        await callRpc('user_remove_push_subscription', {
          p_session_token: session.token,
          p_endpoint: subscriptionJson.endpoint
        });
      }

      setIsSubscribed(false);
      console.log('[Push] Unsubscribed successfully');
      return true;

    } catch (err) {
      console.error('[Push] Error unsubscribing:', err);
      setError('Error al desactivar notificaciones');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    requestPermission
  };
};
